import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  CoverSplash,
  DEFAULT_SPLASH,
  isAllowedDuringLockdown,
} from "@/components/siteMode/parts";

/**
 * Renders the public site behind a configurable cover when the backend
 * reports site-mode != "live". Admins always bypass. A small whitelist of
 * paths (login, contact, /admin/*) is always allowed so operators can get
 * in even when the site is locked.
 */
export default function SiteModeGate({ children }) {
  const [mode, setMode] = useState({ mode: "live", message: null, cover: null });
  const [loaded, setLoaded] = useState(false);
  const { user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/site/mode");
        if (!cancelled) setMode(data);
      } catch {
        if (!cancelled) setMode({ mode: "live", message: null, cover: null });
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Initial paint: neutral spinner instead of flashing the real page.
  if (!loaded) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-slate-50"
        data-testid="site-mode-loading"
      >
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    );
  }
  if (mode.mode === "live") return children;

  // Admins and a small whitelist of paths bypass the cover.
  if ((user?.role || "").toLowerCase() === "admin") return children;
  if (isAllowedDuringLockdown(location.pathname)) return children;

  const defaults = DEFAULT_SPLASH[mode.mode] || DEFAULT_SPLASH.coming_soon;
  const cover = mode.cover || defaults;
  return (
    <CoverSplash
      modeKey={mode.mode}
      cover={cover}
      message={mode.message}
    />
  );
}
